import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, AlertCircle, Calendar as CalendarIcon, Download } from "lucide-react";
import {
  useFulfillCheckoutSession,
  useListMyEnrollments,
  getListMyEnrollmentsQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@clerk/react";
import { enrollmentIcsUrl, formatLocalSessionTime } from "@/lib/calendar";

type FulfillState =
  | { kind: "loading" }
  | { kind: "success"; courseSlug: string; enrollmentId: number }
  | { kind: "error"; message: string };

export default function CheckoutSuccess() {
  const [, setLocation] = useLocation();
  const { isLoaded, isSignedIn } = useAuth();
  const fulfill = useFulfillCheckoutSession();
  const queryClient = useQueryClient();
  const [state, setState] = useState<FulfillState>({ kind: "loading" });

  const sessionId = new URLSearchParams(window.location.search).get(
    "session_id",
  );

  // One-shot latch: React StrictMode runs effects twice in dev. The fulfill
  // endpoint is idempotent server-side, but firing two requests is wasteful
  // and confusing. This guard ensures we trigger fulfill exactly once per
  // mount session.
  const startedRef = useRef(false);

  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      setLocation(`/sign-in?redirect_url=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    if (!sessionId) {
      setState({ kind: "error", message: "Missing session id." });
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;
    fulfill.mutate(
      { id: sessionId },
      {
        onSuccess: (data) => {
          queryClient.invalidateQueries({
            queryKey: getListMyEnrollmentsQueryKey(),
          });
          setState({
            kind: "success",
            courseSlug: data.courseSlug,
            enrollmentId: data.enrollment.id,
          });
        },
        onError: (err: any) => {
          setState({
            kind: "error",
            message: err?.data?.error || "Could not confirm your payment.",
          });
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, isSignedIn, sessionId]);

  return (
    <Layout>
      <div className="container mx-auto px-4 py-32 max-w-xl text-center">
        {state.kind === "loading" && (
          <div className="space-y-4">
            <Loader2 className="w-10 h-10 animate-spin mx-auto text-primary motion-reduce:animate-none" />
            <h1 className="font-display text-3xl font-bold">Confirming payment…</h1>
            <p className="text-muted-foreground">Hold on a moment.</p>
          </div>
        )}
        {state.kind === "success" && (
          <SuccessPanel
            courseSlug={state.courseSlug}
            enrollmentId={state.enrollmentId}
          />
        )}
        {/* see SuccessPanel below for the full success view */}
        {state.kind === "error" && (
          <div className="space-y-4">
            <div className="w-16 h-16 bg-destructive/10 text-destructive rounded-full flex items-center justify-center mx-auto">
              <AlertCircle className="w-8 h-8" />
            </div>
            <h1 className="font-display text-3xl font-bold">Hmm, something went wrong</h1>
            <p className="text-muted-foreground">{state.message}</p>
            <Button asChild variant="outline" className="rounded-full">
              <Link href="/courses">Back to catalog</Link>
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}

function SuccessPanel({
  courseSlug,
  enrollmentId,
}: {
  courseSlug: string;
  enrollmentId: number;
}) {
  // Pull enrollments so we can detect a live cohort and surface the calendar
  // CTA inline. The query was already invalidated by the fulfill mutation, so
  // this is normally a cache hit; the UI just renders without the calendar
  // block on a slow network instead of blocking the success message.
  const { data: enrollments } = useListMyEnrollments({
    query: { queryKey: getListMyEnrollmentsQueryKey() },
  });
  const enrollment = enrollments?.find((e) => e.id === enrollmentId);
  const isLive = enrollment?.course.format === "live";
  const nextSession = enrollment?.upcomingSessions?.[0];

  return (
    <div className="space-y-6">
      <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto">
        <CheckCircle2 className="w-8 h-8" />
      </div>
      <h1 className="font-display text-4xl font-bold tracking-tight">You&rsquo;re in!</h1>
      <p className="text-muted-foreground text-lg">
        Payment confirmed. Your enrollment is active.
      </p>

      {isLive && (
        <div className="bg-card border rounded-2xl p-6 text-left max-w-md mx-auto">
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-primary mb-3">
            <CalendarIcon className="w-3.5 h-3.5" />
            Don&rsquo;t miss your live sessions
          </div>
          {nextSession ? (
            <p className="text-sm text-muted-foreground mb-4">
              First session:{" "}
              <span className="font-medium text-foreground">
                {formatLocalSessionTime(nextSession.zoomScheduledAt)}
              </span>
            </p>
          ) : (
            <p className="text-sm text-muted-foreground mb-4">
              Your teacher will publish the schedule soon. Add the calendar now
              and it&rsquo;ll fill in as sessions are scheduled.
            </p>
          )}
          <Button asChild variant="outline" className="w-full rounded-full">
            <a href={enrollmentIcsUrl(enrollmentId)} download>
              <Download className="w-4 h-4 mr-2" /> Download .ics calendar invite
            </a>
          </Button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
        <Button asChild size="lg" className="rounded-full">
          <Link href={`/learn/${courseSlug}`}>Open Classroom →</Link>
        </Button>
        <Button asChild size="lg" variant="outline" className="rounded-full">
          <Link href="/my-classes">My Classes</Link>
        </Button>
      </div>
    </div>
  );
}
